import { Button } from "@nextui-org/react";
import React from "react";

/**
 * Standardized Cancel Button Component
 * Used consistently across the application for cancel actions
 */
export const StandardCancelButton = ({
  onPress,
  children = "Cancel",
  isDisabled = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="default"
      variant="light"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      className={`text-slate-600 hover:text-slate-800 ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Delete Button Component
 * Used consistently across the application for delete actions
 */
export const StandardDeleteButton = ({
  onPress,
  children = "Delete",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  showIcon = true,
  ...props
}) => {
  return (
    <Button
      color="danger"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`bg-red-600 hover:bg-red-700 text-white ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Delete Button with Icon
 * Used for inline delete actions in tables and lists
 */
export const StandardDeleteIconButton = ({
  onClick,
  isDisabled = false,
  tooltipText = "Delete Item",
  size = "sm",
  className = "",
  ...props
}) => {
  return (
    <Button
      color="danger"
      variant="flat"
      size={size}
      onClick={onClick}
      isDisabled={isDisabled}
      className={`text-red-600 hover:text-red-700 ${className}`}
      {...props}
    >
      🗑️ {tooltipText}
    </Button>
  );
};

/**
 * Standardized Primary Action Button Component
 * Used for main actions like Save, Create, Update, Add, etc.
 */
export const StandardPrimaryButton = ({
  onPress,
  children = "Save",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="primary"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`bg-emerald-600 hover:bg-emerald-700 text-white ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Secondary Action Button Component
 * Used for secondary actions like Edit, Modify, etc.
 */
export const StandardSecondaryButton = ({
  onPress,
  children = "Edit",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="secondary"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`bg-slate-600 hover:bg-slate-700 text-white ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Success Action Button Component
 * Used for success actions like Confirm, Approve, etc.
 */
export const StandardSuccessButton = ({
  onPress,
  children = "Confirm",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="success"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`bg-green-600 hover:bg-green-700 text-white ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Save Button Component
 * Used specifically for save actions
 */
export const StandardSaveButton = ({
  onPress,
  children = "Save",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardPrimaryButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardPrimaryButton>
  );
};

/**
 * Standardized Create Button Component
 * Used specifically for create actions
 */
export const StandardCreateButton = ({
  onPress,
  children = "Create",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardPrimaryButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardPrimaryButton>
  );
};

/**
 * Standardized Update Button Component
 * Used specifically for update actions
 */
export const StandardUpdateButton = ({
  onPress,
  children = "Update",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardPrimaryButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardPrimaryButton>
  );
};

/**
 * Standardized Add Button Component
 * Used specifically for add actions
 */
export const StandardAddButton = ({
  onPress,
  children = "Add",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardPrimaryButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardPrimaryButton>
  );
};

/**
 * Standardized OK Button Component
 * Used specifically for OK/Confirm actions
 */
export const StandardOKButton = ({
  onPress,
  children = "OK",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardSuccessButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardSuccessButton>
  );
};

/**
 * Standardized Submit Button Component
 * Used specifically for submit actions
 */
export const StandardSubmitButton = ({
  onPress,
  children = "Submit",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <StandardPrimaryButton
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={className}
      size={size}
      {...props}
    >
      {children}
    </StandardPrimaryButton>
  );
};

/**
 * Standardized Test Button Component
 * Used specifically for testing actions
 */
export const StandardTestButton = ({
  onPress,
  children = "Test",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="warning"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`bg-orange-600 hover:bg-orange-700 text-white ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Close Button Component
 * Used specifically for closing modals, drawers, etc.
 */
export const StandardCloseButton = ({
  onPress,
  children = "Close",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="default"
      variant="bordered"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`border-emerald-300 text-emerald-700 hover:border-emerald-400 ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * Standardized Copy Button Component
 * Used specifically for copy actions
 */
export const StandardCopyButton = ({
  onPress,
  children = "Copy",
  isDisabled = false,
  isLoading = false,
  className = "",
  size = "sm",
  ...props
}) => {
  return (
    <Button
      color="default"
      variant="light"
      size={size}
      onPress={onPress}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={`text-emerald-600 hover:text-emerald-700 ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
};
